/** One journal entry, its history, and the actions its status allows. */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Pencil, Send, Trash2, Undo2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount } from '../../lib/money';
import { dateTime, shortDate } from '../../lib/format';
import { ReasonDialog } from '../../components/ReasonDialog';
import { Alert, Button, Card, Modal, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th } from '../../components/ui';
import { Attachments } from '../../components/Attachments';
import { SOURCE_LABEL, STATUS_LABEL, STATUS_TONE, type JournalDetail } from './journalTypes';

export function JournalDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'void' | 'reject' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entry = useQuery({ queryKey: ['journal', id], queryFn: () => api<JournalDetail>(`/journals/${id}`) });

  const act = useMutation({
    mutationFn: ({ path, body, method = 'POST' }: { path: string; body?: unknown; method?: 'POST' | 'DELETE' }) =>
      api<JournalDetail>(`/journals/${id}${path}`, { method, body }),
    onSuccess: (_data, vars) => {
      setDialog(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['journals'] });
      if (vars.method === 'DELETE') navigate('/journals', { replace: true });
      else void queryClient.invalidateQueries({ queryKey: ['journal', id] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'That did not work.'),
  });

  if (entry.isLoading) return <Skeleton className="h-96 w-full" />;
  if (entry.isError || !entry.data) return <Alert tone="critical">Could not load this entry.</Alert>;
  const e = entry.data;
  const manual = e.source === 'MANUAL' || e.source === 'OPENING';
  const isMaker = e.createdById === user?.id;

  return (
    <>
      <PageHeader
        title={e.number ?? (e.source === 'OPENING' ? 'Opening balances (draft)' : 'Draft journal entry')}
        description={`${SOURCE_LABEL[e.source] ?? e.source} · ${shortDate(e.date)} · ${e.period.name}`}
        actions={
          <>
            <StatusChip tone={STATUS_TONE[e.status]}>{STATUS_LABEL[e.status]}</StatusChip>
            {e.status === 'DRAFT' && manual && can('journals.create') && (
              <>
                {e.source === 'MANUAL' && (
                  <Link to={`/journals/${e.id}/edit`}>
                    <Button variant="secondary"><Pencil aria-hidden="true" className="h-4 w-4" /> Edit</Button>
                  </Link>
                )}
                <Button variant="secondary" onClick={() => setDialog('discard')}><Trash2 aria-hidden="true" className="h-4 w-4" /> Discard</Button>
                <Button busy={act.isPending} onClick={() => act.mutate({ path: '/post' })}>
                  <Send aria-hidden="true" className="h-4 w-4" /> Post
                </Button>
              </>
            )}
            {e.status === 'PENDING_APPROVAL' && can('journals.approve') && !isMaker && (
              <>
                <Button variant="secondary" onClick={() => setDialog('reject')}>Send back</Button>
                <Button busy={act.isPending} onClick={() => act.mutate({ path: '/approve' })}>
                  <CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Approve and post
                </Button>
              </>
            )}
            {e.status === 'POSTED' && manual && can('void.approve') && (
              <Button variant="danger" onClick={() => setDialog('void')}><Undo2 aria-hidden="true" className="h-4 w-4" /> Void</Button>
            )}
          </>
        }
      />
      {error && !dialog && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {e.status === 'PENDING_APPROVAL' && isMaker && (
        <div className="mb-4"><Alert tone="warning">This entry is above your approval limit. Someone with approval rights must approve it.</Alert></div>
      )}
      {e.status === 'VOIDED' && (
        <div className="mb-4">
          <Alert tone="critical">
            Voided by {e.voidedBy ?? '—'} on {dateTime(e.voidedAt)}: {e.voidReason}.
            {e.reversals.map((r) => (
              <span key={r.id}> Reversed by <Link className="underline" to={`/journals/${r.id}`}>{r.number}</Link>.</span>
            ))}
          </Alert>
        </div>
      )}
      {e.reversalOf && (
        <div className="mb-4"><Alert tone="info">This entry reverses <Link className="underline" to={`/journals/${e.reversalOf.id}`}>{e.reversalOf.number}</Link>.</Alert></div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <Card>
          {e.narration && <p className="mb-4 text-sm text-slate-700 dark:text-slate-200">{e.narration}</p>}
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>#</Th>
                <Th>Account</Th>
                <Th>Description</Th>
                <Th align="right">Debit</Th>
                <Th align="right">Credit</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {e.lines.map((l) => (
                <tr key={l.id}>
                  <Td className="text-slate-400">{l.lineNo}</Td>
                  <Td className="whitespace-nowrap"><span className="font-medium">{l.accountCode}</span> {l.accountName}</Td>
                  <Td>
                    {l.description || '—'}
                    {l.dimensionLabels.length > 0 && <span className="block text-xs text-slate-500">{l.dimensionLabels.join(' · ')}</span>}
                  </Td>
                  <Td align="right"><Money>{amount(l.debit, true)}</Money></Td>
                  <Td align="right"><Money>{amount(l.credit, true)}</Money></Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 font-semibold dark:border-slate-800">
                <Td className="pt-3">{''}</Td>
                <Td>Total</Td>
                <Td>{''}</Td>
                <Td align="right"><Money>{amount(e.totalDebit)}</Money></Td>
                <Td align="right"><Money>{amount(e.totalCredit)}</Money></Td>
              </tr>
            </tfoot>
          </TableWrap>
        </Card>
        <Card title="History">
          <dl className="space-y-3 text-sm">
            <div><dt className="text-slate-500">Drafted by</dt><dd>{e.createdBy ?? '—'}</dd></div>
            {e.approvedBy && <div><dt className="text-slate-500">Approved by</dt><dd>{e.approvedBy}</dd></div>}
            {e.postedBy && <div><dt className="text-slate-500">Posted</dt><dd>{e.postedBy}, {dateTime(e.postedAt)}</dd></div>}
            {e.reference && <div><dt className="text-slate-500">Reference</dt><dd>{e.reference}</dd></div>}
            {e.autoReverseDate && <div><dt className="text-slate-500">Reverses on</dt><dd>{shortDate(e.autoReverseDate)}</dd></div>}
          </dl>
        </Card>
      </div>

      {dialog === 'void' && (
        <ReasonDialog
          title={`Void ${e.number}?`}
          description="A reversing entry is posted and this one is marked void. Both stay on the books."
          confirmLabel="Void entry"
          withDate
          error={error}
          busy={act.isPending}
          onClose={() => { setDialog(null); setError(null); }}
          onConfirm={(reason, date) => act.mutate({ path: '/void', body: { reason, date } })}
        />
      )}
      {entry.data && <Attachments entity="journal_entry" entityId={entry.data.id} canAttach={can('journals.create', 'journals.post')} />}
      {dialog === 'reject' && (
        <ReasonDialog
          title="Send back to the maker?"
          description="The entry returns to draft so it can be corrected."
          confirmLabel="Send back"
          tone="primary"
          error={error}
          busy={act.isPending}
          onClose={() => { setDialog(null); setError(null); }}
          onConfirm={(reason) => act.mutate({ path: '/reject', body: { reason } })}
        />
      )}
      {dialog === 'discard' && (
        <Modal title="Discard this draft?" description="It was never posted, so nothing reaches the ledger." onClose={() => setDialog(null)}>
          {error && <div className="mb-3"><Alert tone="critical">{error}</Alert></div>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDialog(null)}>Keep it</Button>
            <Button variant="danger" busy={act.isPending} onClick={() => act.mutate({ path: '', method: 'DELETE' })}>Discard</Button>
          </div>
        </Modal>
      )}
    </>
  );
}
